import { mapState } from 'vuex';
import multiSearchMixin from '@/mixins/multiSearchDigitalTwins';
import { isNullOrEmpty } from '@/utils/vuex';
import { localizeDate } from '@/utils/date';
import { calculateNextExecutionTimes } from '@/utils/recurringTasks';
import { isEqual } from 'lodash';
const COUNT_NEXT_EXECUTIONS = 3;
const FREQUENCY_OPTIONS = ['yearly', 'monthly', 'weekly', 'daily']; // 'hourly' is hidden, might be available in future

export default {
  name: 'CoreCsvExportFrequency',
  mixins: [multiSearchMixin],
  props: {
    value: {
      type: Object,
      default: () => ({
        frequency: 'monthly',
        month: null,
        day: null,
        hour: null,
        minute: null
      })
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      internalModel: {
        ...this.value
      },
      countNextExecutions: COUNT_NEXT_EXECUTIONS,
      frequencyOptions: FREQUENCY_OPTIONS,
      formIsValid: false,
      disabledTimeInputs: [],
      requiredTimeRules: [],
      nextExecutionTimes: [],
      daysList: [{
        dayId: 1,
        dayText: this.$i18n_t('Common.Monday')
      }, {
        dayId: 2,
        dayText: this.$i18n_t('Common.Tuesday')
      }, {
        dayId: 3,
        dayText: this.$i18n_t('Common.Wednesday')
      }, {
        dayId: 4,
        dayText: this.$i18n_t('Common.Thursday')
      }, {
        dayId: 5,
        dayText: this.$i18n_t('Common.Friday')
      }, {
        dayId: 6,
        dayText: this.$i18n_t('Common.Saturday')
      }, {
        dayId: 7,
        dayText: this.$i18n_t('Common.Sunday')
      }]
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color']),
    computedRules() {
      return {
        rangeMinute: v => v <= 59 && v >= 0 || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.invalidField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.minute')
        }),
        rangeHour: v => v <= 23 && v >= 0 || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.invalidField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.hour')
        }),
        rangeDay: v => v <= 31 && v >= 1 || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.invalidField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.day')
        }),
        rangeMonth: v => v <= 12 && v >= 1 || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.invalidField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.month')
        }),
        rangeWeekDay: v => v <= 7 && v >= 1 || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.invalidField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.weekDay')
        }),
        reqMinute: v => v !== undefined && v !== null && v !== '' || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.requiredField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.minute')
        }),
        reqHour: v => v !== undefined && v !== null && v !== '' || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.requiredField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.hour')
        }),
        reqDay: v => v !== undefined && v !== null && v !== '' || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.requiredField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.day')
        }),
        reqMonth: v => v !== undefined && v !== null && v !== '' || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.requiredField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.month')
        }),
        reqWeekDay: v => v !== undefined && v !== null && v !== '' || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.requiredField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.weekDay')
        })
      };
    }
  },
  watch: {
    internalModel: {
      handler() {
        this.setDisabledFields();
        this.setRequiredRules();
        this.$refs.formFrequency.validate();
        this.$nextTick(() => {
          this.getNextExecutionTimes();
          this.$emit('input', {
            ...this.internalModel,
            formIsValid: this.formIsValid
          });
        });
      },
      deep: true
    },
    value: {
      handler(newValue) {
        if (!isEqual(newValue, this.internalModel)) {
          this.internalModel = {
            ...this.value
          };
        }
      },
      deep: true
    }
  },
  mounted() {
    this.setRequiredRules();
    this.setDisabledFields();
    this.$refs.formFrequency.validate();
    this.getNextExecutionTimes();
    this.$emit('input', {
      ...this.internalModel,
      formIsValid: this.formIsValid
    });
  },
  methods: {
    localizeDate,
    isNullOrEmpty,
    setRequiredRules() {
      this.requiredTimeRules = {};
      if (this.internalModel.repeat === 'hourly') {
        this.requiredTimeRules['minute'] = [this.computedRules.reqMinute, this.computedRules.rangeMinute];
      } else if (this.internalModel.repeat === 'daily') {
        this.requiredTimeRules['hour'] = [this.computedRules.reqHour, this.computedRules.rangeHour];
        this.requiredTimeRules['minute'] = [this.computedRules.reqMinute, this.computedRules.rangeMinute];
      } else if (this.internalModel.repeat === 'monthly') {
        this.requiredTimeRules['day'] = [this.computedRules.reqDay, this.computedRules.rangeDay];
        this.requiredTimeRules['hour'] = [this.computedRules.reqHour, this.computedRules.rangeHour];
        this.requiredTimeRules['minute'] = [this.computedRules.reqMinute, this.computedRules.rangeMinute];
      } else if (this.internalModel.repeat === 'weekly') {
        this.requiredTimeRules['weekDay'] = [this.computedRules.reqWeekDay, this.computedRules.rangeWeekDay];
        this.requiredTimeRules['hour'] = [this.computedRules.reqHour, this.computedRules.rangeHour];
        this.requiredTimeRules['minute'] = [this.computedRules.reqMinute, this.computedRules.rangeMinute];
      } else {
        this.requiredTimeRules['month'] = [this.computedRules.reqMonth, this.computedRules.rangeMonth];
        this.requiredTimeRules['day'] = [this.computedRules.reqDay, this.computedRules.rangeDay,
        // Extra rule for February
        v => this.month === 2 && v <= 29 && v >= 1 || this.month !== 2 || this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.invalidField', {
          item: this.$i18n_t('BackgroundJobs.ScheduledExports.dialog.frequency.day')
        })];
        this.requiredTimeRules['hour'] = [this.computedRules.reqHour, this.computedRules.rangeHour];
        this.requiredTimeRules['minute'] = [this.computedRules.reqMinute, this.computedRules.rangeMinute];
      }
    },
    setDisabledFields() {
      if (this.internalModel.repeat === 'hourly') {
        this.disabledTimeInputs = ['month', 'day', 'hour'];
      } else if (this.internalModel.repeat === 'daily') {
        this.disabledTimeInputs = ['month', 'day'];
      } else if (this.internalModel.repeat === 'weekly') {
        this.disabledTimeInputs = ['month', 'day'];
      } else if (this.internalModel.repeat === 'monthly') {
        this.disabledTimeInputs = ['month'];
      } else {
        this.disabledTimeInputs = [];
      }
    },
    getNextExecutionTimes() {
      if (!this.formIsValid) {
        this.nextExecutionTimes = [];
        return;
      }
      try {
        const nextExecutionTimes = calculateNextExecutionTimes(this.internalModel.repeat, this.countNextExecutions, this.internalModel.month, this.internalModel.day, this.internalModel.hour, this.internalModel.minute, this.internalModel.weekDay);
        this.nextExecutionTimes = nextExecutionTimes;
      } catch (error) {
        console.error(error);
        this.nextExecutionTimes = [];
      }
    }
  }
};